import * as THREE from 'three';
import { lookDotTo, reachDistance, type Xyz } from '../interact/Interact';

export const SPEAR_RECOVER_S = 0.7;
export const SPEAR_DAMAGE = 22;
export const SPEAR_REACH = 2.8;
export const SPEAR_STAMINA = 12;
/** Cosine of the thrust cone (~56°). Camera-forward, else yaw +Z. */
export const SPEAR_LOOK_MIN = 0.12;

const _look = new THREE.Vector3();

export type SpearTarget = {
  live: boolean;
  x: number;
  z: number;
  position: Xyz;
  takeHit(amount: number): void;
  interactTarget?(): { position: Xyz; radius: number };
};

export type SpearStamina = {
  stamina?: number;
  spendStamina?(amount: number): boolean;
};

export type SpearVisual = {
  punch?(): void;
};

export type SpearAttackCtx = {
  held: string | null;
  camera: THREE.Camera;
  player: Xyz;
  yaw?: number;
  dinos: readonly SpearTarget[];
  stamina?: SpearStamina | null;
  visual?: SpearVisual | null;
};

export class Spear {
  recover = 0;
  attacked = 0;
  lastHit = false;

  tick(dt: number): void {
    if (!Number.isFinite(dt) || dt <= 0) {
      return;
    }
    this.recover = Math.max(0, this.recover - dt);
  }

  drive(dt: number, attack: boolean, ctx: SpearAttackCtx): boolean {
    this.tick(dt);
    return attack ? this.tryAttack(ctx) : false;
  }

  tryAttack(ctx: SpearAttackCtx): boolean {
    this.lastHit = false;
    if (this.recover > 0) {
      return false;
    }
    if (ctx.held === 'spear') {
      spendIfPresent(ctx.stamina, SPEAR_STAMINA);
    }
    this.recover = SPEAR_RECOVER_S;
    this.attacked += 1;
    ctx.visual?.punch?.();
    const prey = firstDinoInCone(ctx.dinos, ctx.camera, ctx.player, ctx.yaw);
    this.lastHit = !!prey;
    prey?.takeHit(ctx.held === 'spear' ? SPEAR_DAMAGE : 8);
    return true;
  }
}

/** Closest live dino inside 2.1 m + look cone. */
export function firstDinoInCone(
  dinos: readonly SpearTarget[],
  camera: THREE.Camera,
  player: Xyz,
  yaw?: number,
): SpearTarget | null {
  lookDir(camera, yaw, _look);
  let best: SpearTarget | null = null;
  let bestDist = Infinity;
  for (const dino of dinos) {
    if (!dino.live) {
      continue;
    }
    const dist = coneReach(player, dino);
    if (dist > SPEAR_REACH) {
      continue;
    }
    const high = dino.position.y - player.y > 1.1;
    const origin = high ? { x: player.x, y: dino.position.y, z: player.z } : player;
    const look = high ? _look.clone().setY(0) : _look;
    if (high && look.lengthSq() > 1e-8) {
      look.normalize();
    }
    if (lookDotTo(origin, look, dino.position) < SPEAR_LOOK_MIN) {
      continue;
    }
    if (dist < bestDist) {
      best = dino;
      bestDist = dist;
    }
  }
  return best;
}

function lookDir(camera: THREE.Camera, yaw: number | undefined, out: THREE.Vector3): THREE.Vector3 {
  if (Number.isFinite(yaw)) {
    const facing = yaw as number;
    return out.set(-Math.sin(facing), 0, -Math.cos(facing));
  }
  camera.getWorldDirection(out);
  return out;
}

function coneReach(player: Xyz, dino: SpearTarget): number {
  if (typeof dino.interactTarget === 'function') {
    const target = dino.interactTarget();
    return reachDistance(player, {
      id: 'dino',
      prompt: '',
      position: target.position,
      radius: target.radius,
      live: true,
    });
  }
  return Math.hypot(dino.x - player.x, dino.z - player.z);
}

function spendIfPresent(pool: SpearStamina | null | undefined, amount: number): boolean {
  if (!pool) {
    return true;
  }
  if (typeof pool.spendStamina === 'function') {
    return pool.spendStamina(amount);
  }
  return true;
}
